import React, {useCallback, useState} from 'react';
import {ModelMetadata, modelMetadataAddOption, modelMetadataOptionsLabels} from '../../../../../../store/modelMetadata';
import {useDispatch} from 'react-redux';
import {messageAdd, MessageTypes} from '../../../../../../store/message';
import {useMounted} from '../../../../../../hooks/useMounted';
import {trackModelMetadataOptionAdded} from '../../../../../../tracking/tracking';
import UpdateOptionForm from '../updateOption/UpdateOptionForm';
import {ManageMetadataActionProps} from '../ActionMenuTypes';
import {CloseOutlined, PlusOutlined} from '@ant-design/icons';
import styles from './AddOption.module.scss';

const AddOption: React.FC<Omit<ManageMetadataActionProps, 'value'>> = ({
  property,
  onSuccess,
  onCancel,
  refreshOptionsCount,
}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const mounted = useMounted();

  const handleSubmit = useCallback(
    (value: string, metadata?: ModelMetadata) => {
      setSubmitting(true);
      return modelMetadataAddOption(dispatch, property, value, metadata)
        .then(() => {
          dispatch(
            messageAdd(`${value} has been added into ${modelMetadataOptionsLabels[property]}`, MessageTypes.SUCCESS),
          );
          trackModelMetadataOptionAdded();
          onSuccess();
          if (refreshOptionsCount) refreshOptionsCount();
        })
        .catch(() => null)
        .finally(() => {
          mounted.current && setSubmitting(false);
        });
    },
    [dispatch, onSuccess, property, mounted, refreshOptionsCount],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>Add {modelMetadataOptionsLabels[property]} Option</h2>
        </div>
        <button className={styles.closeBtn} onClick={onCancel} aria-label="Close">
          <CloseOutlined />
        </button>
      </div>
      <div className={styles.formWrapper}>
        <p className={styles.subtitle}>This action won&apos;t affect any existing model entry</p>

        <UpdateOptionForm
          property={property}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          submitButtonText="Add Option"
          submitButtonIcon={<PlusOutlined />}
          isAddMode={true}
        />
      </div>
    </div>
  );
};

export default AddOption;
